"use client";

import { useState, useEffect } from "react";
import {
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Mail,
  Phone,
  MapPin,
  ChevronUp,
  Heart,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BsTwitterX } from "react-icons/bs";

const SERVICES_THEME = {
  background: "bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500",
  accent: "text-indigo-100",
  border: "border-indigo-500/30",
  hover: "hover:text-indigo-200",
};

const LOGISTICS_THEME = {
  background: "bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500",
  accent: "text-yellow-100",
  border: "border-yellow-500/30",
  hover: "hover:text-yellow-200",
};

const BUSINESS_THEME = {
  background: "bg-gradient-to-r from-emerald-600 to-teal-600",
  accent: "text-green-100",
  border: "border-green-500/30",
  hover: "hover:text-green-200",
};

const PRODUCTS_THEME = {
  background: "bg-gradient-to-r from-blue-600 to-blue-800",
  accent: "text-blue-100",
  border: "border-blue-500/30",
  hover: "hover:text-blue-200",
};

const FRANCHISE_THEME = {
  background: "bg-gradient-to-r from-indigo-600 to-purple-600",
  accent: "text-purple-100",
  border: "border-purple-500/30",
  hover: "hover:text-purple-200",
};

const SELLER_THEME = {
  background: "bg-gradient-to-r from-blue-600 to-blue-800",
  accent: "text-blue-100",
  border: "border-blue-500/30",
  hover: "hover:text-blue-200",
};

// Page color themes (same as navbar)
const PAGE_THEMES = {
  "/": {
    background: "bg-gradient-to-r from-blue-600 to-blue-800",
    accent: "text-blue-100",
    border: "border-blue-500/30",
    hover: "hover:text-blue-200",
  },

  "/localsuppliers": {
    background: "bg-gradient-to-r from-orange-500 to-orange-700",
    accent: "text-orange-100",
    border: "border-orange-400/30",
    hover: "hover:text-orange-200",
  },

  "/products": PRODUCTS_THEME,
  "/products/:productid": PRODUCTS_THEME,

  "/sellers": SELLER_THEME,
  "/sellers/:sellerId": SELLER_THEME,
  // "/sellerdashboard": SELLER_THEME,
  // "/selleronboarding": SELLER_THEME,

  "/franchise": FRANCHISE_THEME,
  "/franchise/:franchiseId": FRANCHISE_THEME,
  // "/dashboard/franchise": FRANCHISE_THEME,
  // "/businessfranchiseonboarding": FRANCHISE_THEME,

  "/business": BUSINESS_THEME,
  "/business/:id": BUSINESS_THEME,
  // "/dashboard/business-services": BUSINESS_THEME,
  // "/businessfranchiseonboarding": BUSINESS_THEME,

  "/logistics": LOGISTICS_THEME,
  "/logistics/:id": LOGISTICS_THEME,
  // "/logisticsDashboard": LOGISTICS_THEME,
  // "/logisticsonboarding": LOGISTICS_THEME,

  "/services": SERVICES_THEME,
  "/services/:id": SERVICES_THEME,
  // "/dashboard/business-services": SERVICES_THEME,
  // "/serviceonboarding": SERVICES_THEME,

  default: {
    background: "bg-gradient-to-r from-blue-600 to-blue-800",
    accent: "text-blue-100",
    border: "border-blue-500/30",
    hover: "hover:text-blue-200",
  },
};

export default function Footer({ type }) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  // const [currentPath, setCurrentPath] = useState("/")
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Update current path
  // useEffect(() => {
  //   setCurrentPath(window.location.pathname)
  // }, [])

  // Handle scroll to top visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Get current page theme
  const getCurrentTheme = () => {
    // Exact match first
    if (PAGE_THEMES[currentPath]) return PAGE_THEMES[currentPath];

    // Dynamic route matching
    if (currentPath.startsWith("/products/"))
      return PAGE_THEMES["/products/:productid"];
    if (currentPath.startsWith("/sellers/"))
      return PAGE_THEMES["/sellers/:sellerId"];
    if (currentPath.startsWith("/franchise/"))
      return PAGE_THEMES["/franchise/:franchiseId"];
    if (currentPath.startsWith("/business/"))
      return PAGE_THEMES["/business/:id"];
    if (currentPath.startsWith("/logistics/"))
      return PAGE_THEMES["/logistics/:id"];
    if (currentPath.startsWith("/services/"))
      return PAGE_THEMES["/services/:id"];

    // Fallback
    return PAGE_THEMES.default;
  };

  let theme = getCurrentTheme();

  if (type === "business") {
    theme = BUSINESS_THEME;
  } else if (type === "franchise") {
    theme = FRANCHISE_THEME;
  }

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const footerSections = [
    {
      title: "Support",
      links: [
        { name: "Get Support", href: "/support?tab=Get%20Support" },

        { name: "Help Center", href: "/support?tab=Help%20Center" },
        {
          name: "Check Order Status",
          href: "/support?tab=Check%20Order%20Status",
        },
        { name: "Refund", href: "/support?tab=Refund" },
        { name: "Report", href: "/support?tab=Report" },
        {
          name: "Buyer Support FAQs",
          href: "/faqPage", // makes it act like a link
          onClick: (e) => {
            e.preventDefault() // prevent normal reload
            navigate("/faqPage", { state: { type: "buyer" } })
          },
        },
        { name: "Secure Payments (Escrow)", href: "/applications/escrow-support" },
      ],
    },
    {
      title: "Payments and Protection",
      links: [
        {
          name: "Payments and Protection",
          href: "/payments?tab=Payments%20and%20Protection",
        },
        {
          name: "Safe and easy payments",
          href: "/payments?tab=Safe%20and%20easy%20payments",
        },
        {
          name: "Payments and Transaction FAQs",
          onClick: () => navigate("/faqPage", { state: { type: "payments" } }),
        },
        // { name: "Money-back policy", href: "/payments?tab=Money-back%20policy" },
        // { name: "On-time shipping", href: "/payments?tab=On-time%20shipping" },
        // { name: "Design systems", href: "/payments?tab=Design%20systems" },
        // { name: "After-sales protections", href: "/payments?tab=After-sales%20protections" },
        // { name: "Product monitoring services", href: "/payments?tab=Product%20monitoring%20services" },
      ],
    },
    {
      title: "Merzado Services",
      links: [
        {
          name: "Source on Merzado",
          href: "/merzado-services?tab=Source%20on%20Merzado",
        },
        //
        { name: "RFQ", href: "/RFQMainPage" },
        // { name: "Membership Program", href: "/merzado-services?tab=Membership%20Program" },
        { name: "Membership Program", href: "/Subscriptions" },
        {
          name: "Merzado Logistics",
          href: "/merzado-services?tab=Merzado%20Logistics",
        },
        // { name: "Sales and tax", href: "/merzado-services?tab=Sales%20and%20tax" },
      ],
    },
    {
      title: "Get to Know",
      links: [
        { name: "About Us", href: "/get-to-know?tab=About%20Us" },
        { name: "Contact Us", href: "/get-to-know?tab=Contact%20Us" },
        // { name: "Business", href: "/get-to-know?tab=Business" },
        { name: "Careers", href: "/get-to-know?tab=Careers" },
      ],
    },
  ];
  // const footerSections = [
  //   {
  //     title: "Support",
  //     links: [
  //       { name: "Get Support", href: "/support" },
  //       { name: "Help Center", href: "/help" },
  //       { name: "Live Chat", href: "/chat" },
  //       { name: "Check Order Status", href: "/order-status" },
  //       { name: "Refund", href: "/refund" },
  //       { name: "Report", href: "/report" },
  //     ],
  //   },
  //   {
  //     title: "Payments and Protection",
  //     links: [
  //       { name: "Payments and Protection", href: "/payments" },
  //       { name: "Safe and easy payments", href: "/safe-payments" },
  //       { name: "Money-back policy", href: "/money-back" },
  //       { name: "On-time shipping", href: "/shipping" },
  //       { name: "Design systems", href: "/design-systems" },
  //       { name: "After-sales protections", href: "/after-sales" },
  //       { name: "Product monitoring services", href: "/monitoring" },
  //     ],
  //   },
  //   {
  //     title: "Merzado Services",
  //     links: [
  //       { name: "Source on Merzado", href: "/source" },
  //       { name: "RFQ", href: "/rfq" },
  //       { name: "Membership Program", href: "/membership" },
  //       { name: "Merzado Logistics", href: "/logistics" },
  //       { name: "Sales and tax", href: "/sales-tax" },
  //     ],
  //   },
  //   {
  //     title: "Get to Know",
  //     links: [
  //       { name: "About Us", href: "/about" },
  //       { name: "Contact Us", href: "/contact" },
  //       { name: "Business", href: "/business" },
  //       { name: "Careers", href: "/careers" },
  //     ],
  //   },
  // ]

  const socialLinks = [
    {
      name: "Facebook",
      icon: Facebook,
      href: "https://www.facebook.com/profile.php?id=61569957466438",
    },
    { name: "Twitter", icon: BsTwitterX, href: "https://x.com/Merzado_com" },
    {
      name: "Instagram",
      icon: Instagram,
      href: "https://www.instagram.com/merzado.com_official/",
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      href: "https://linkedin.com/company/merzado.com",
    },
    { name: "YouTube", icon: Youtube, href: "http://www.youtube.com/@MERZADO" },
  ];

  const HIDDEN_PATHS = [/^\/livestream\/start$/];
  const shouldHideFooter = HIDDEN_PATHS.some((regex) =>
    regex.test(currentPath)
  );
  if (shouldHideFooter) return null;

  return (
    <>
      <footer
        className={`${theme.background} ${theme.accent
          } relative overflow-hidden ${isMobile ? "mb-12" : ""}`}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>
        </div>

        <div className="relative z-10">
          {/* Main Footer Content */}
          <div className="container-custom py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {footerSections.map((section, index) => (
                <div key={index} className="space-y-6">
                  <h3 className="text-xl font-bold text-white mb-6 relative">
                    {section.title}
                    <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-white/30 rounded-full"></div>
                  </h3>
                  <ul className="space-y-1">
                    {section.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <a
                          href={link.href}
                          className={`${theme.hover} transition-all duration-200 text-sm leading-relaxed block hover:translate-x-1 transform`}
                        >
                          {link.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            Newsletter Subscription
            <div className={`mt-2 pt-6 border-t ${theme.border}`}>
              <div className="grid grid-cols-1 lg:grid-cols-1 gap-8 items-center">
                {/* <div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    Stay Connected
                  </h3>
                  <p className="text-lg opacity-90 mb-2">
                    Get the latest updates on new products, exclusive deals, and
                    industry insights.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="email"
                      placeholder="Enter your email address"
                      className="flex-1 p-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-transparent transition-all duration-200"
                    />
                    <button className="p-2 bg-white text-gray-900 rounded-lg font-semibold hover:bg-white/90 transition-all duration-200 transform hover:scale-105 shadow-lg">
                      Subscribe
                    </button>
                  </div>
                </div> */}

                {/* Contact Info */}
                <div className="space-y-2 text-center">
                  <h4 className="text-lg font-semibold text-white mb-4">
                    Contact Information
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-center space-x-3">
                      <Mail className="h-5 w-5 opacity-80" />
                      <span className="text-sm">support@merzado.com</span>
                    </div>
                    <div className="flex items-center justify-center space-x-3">
                      <Phone className="h-5 w-5 opacity-80" />
                      <span className="text-sm">+91 99639 30111</span>
                    </div>
                    <div className="flex items-center justify-center space-x-3">
                      <MapPin className="h-5 w-5 opacity-80" />
                      <span className="text-sm">
                        08th Floor, Sln Terminus, Survey No. 133, Beside Botanical Gardens, Gachibowli, K.v.rangareddy, Seri Lingampally, Telangana, India, 500032.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media & Additional Links */}
            <div className={`mt-4 pt-4 border-t ${theme.border}`}>
              <div className="flex flex-col lg:flex-row justify-between items-center space-y-6 lg:space-y-0">
                {/* Social Media */}
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium opacity-90">
                    Follow Us:
                  </span>
                  <div className="flex space-x-4">
                    {socialLinks.map((social, index) => {
                      const Icon = social.icon;
                      return (
                        <a
                          key={index}
                          href={social.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-200 transform hover:scale-110"
                          aria-label={social.name}
                        >
                          <Icon className="h-5 w-5" />
                        </a>
                      );
                    })}
                  </div>
                </div>

                {/* Additional Links */}
                {/* <div className="flex flex-wrap items-center gap-4 text-sm">
                  <a
                    href="/privacy"
                    className={`${theme.hover} transition-colors duration-200`}
                  >
                    Privacy Policy
                  </a>
                  <a
                    href="/terms-and-services"
                    className={`${theme.hover} transition-colors duration-200`}
                  >
                    Terms of Service
                  </a>
                  <a
                    href="/cookies"
                    className={`${theme.hover} transition-colors duration-200`}
                  >
                    Cookie Policy
                  </a>
                  <a
                    href="/sitemap"
                    className={`${theme.hover} transition-colors duration-200`}
                  >
                    Sitemap
                  </a>
                </div> */}
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <Link
                    to="/policies/privacy-policy"
                    className={`${theme.hover} transition-colors duration-200`}
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    to="/policies/terms-of-service"
                    className={`${theme.hover} transition-colors duration-200`}
                  >
                    Terms of Service
                  </Link>
                  <Link
                    to="/policies/cookie-policy"
                    className={`${theme.hover} transition-colors duration-200`}
                  >
                    Cookie Policy
                  </Link>
                  <Link
                    to="/policies/buyer-terms-conditions"
                    className={`${theme.hover} transition-colors duration-200`}
                  >
                    Buyers Terms & Conditions
                  </Link>
                  <Link
                    to="/policies"
                    className={`${theme.hover} transition-colors duration-200`}
                  >
                    Platform Policies
                  </Link>
                  {/* <Link
                    to="/sitemap"
                    className={`${theme.hover} transition-colors duration-200`}
                  >
                    Sitemap
                  </Link> */}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div
            className={`border-t ${theme.border} bg-black/20 backdrop-blur-sm`}
          >
            <div className="container-custom py-4">
              <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                <div className="flex items-center space-x-2 text-sm">
                  <span>&copy; 2025 Merzado. All rights reserved.</span>
                  {/* <span className="hidden md:inline">|</span>
                  <span className="flex items-center space-x-1">
                    <span>Made with</span>
                    <Heart className="h-4 w-4 text-red-400 fill-current" />
                    <span>for global trade</span>
                  </span> */}
                </div>

                <div className="flex items-center space-x-4 text-sm">
                  {/* <span className="opacity-80">
                    Trusted by 10,000+ businesses worldwide
                  </span> */}
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-green-400 font-medium">
                      All systems operational
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className={`fixed bottom-[1rem] right-[20px] z-50 w-14 h-14 flex items-center justify-center ${theme.background} text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 backdrop-blur-sm border ${theme.border}`}
          aria-label="Scroll to top"
        >
          <ChevronUp className="h-6 w-6" />
        </button>
      )}
    </>
  );
}
